import { useEffect, useMemo, useRef } from 'react';
import { AffiliateCodes, LISTING_HEAP_IDS, STATSIG_EVENT_NAMES } from '@/components/_shared/constants';
import { useComponentVisibilityWithTracking } from '@/hooks/useComponentVisibility';
import { assignRefsToNode } from '@/utils/utils';
import { isEventEnded, isEventLive } from '@/components/Body/utils/bodyUtils';
import type { BasicInfo } from '@/types';
import type { OrganizerEventResponse } from '../types';
import { useMoreOrganizerEventsLazy } from './useMoreOrganizerEventsLazy';

type UseMoreOrganizerEventsSectionModelArgs = {
  eventId: string;
  eventBasicInfo: BasicInfo;
  isBodyRendered?: boolean;
  initialData?: OrganizerEventResponse;
};

export const useMoreOrganizerEventsSectionModel = ({
  eventId,
  eventBasicInfo,
  isBodyRendered,
  initialData,
}: UseMoreOrganizerEventsSectionModelArgs) => {
  const { organizerEvents, isLoading, elementRef } = useMoreOrganizerEventsLazy(
    eventBasicInfo,
    eventId,
    isBodyRendered,
    initialData,
  );

  const eventsToShow = organizerEvents?.events || [];
  const hasEvents = eventsToShow.length > 0;
  const visibilityTrackingRef = useComponentVisibilityWithTracking(undefined, {
    heap: {
      eventName: LISTING_HEAP_IDS.ORGANIZER_MORE_EVENTS_VIEW,
      eventBucketLabel: 'More events from this organizer',
    },
    statsig: {
      eventName: STATSIG_EVENT_NAMES.LISTING_MORE_ORGANIZER_EVENTS_VIEW,
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      assignRefsToNode([elementRef, hasEvents ? visibilityTrackingRef : undefined], node);
    }
  }, [elementRef, hasEvents, visibilityTrackingRef]);

  const affiliateCode = useMemo(() => {
    if (isEventEnded(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    }
    if (isEventLive(eventBasicInfo.status)) {
      return AffiliateCodes.AFF_RELATED_EVENTS_LIVE;
    }
    return undefined;
  }, [eventBasicInfo.status]);

  return {
    affiliateCode,
    containerRef,
    eventsToShow,
    hasEvents,
    isLoading,
    organizerEvents,
  };
};
